import { lazy, Suspense, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Helmet } from 'react-helmet-async';
import { activitiesService, bookmarkService, mediaUrl, registrationService, volunteerService } from '../lib/api-service';
import { useAuth } from '../lib/auth';
import { LoginPromptModal } from '../components/LoginPromptModal';
import { RegistrationModal } from '../components/RegistrationModal';
import { DocumentMeta } from '../hooks/useDocumentMeta';
import type { ActivityDetail } from '../lib/types';
import ReactMarkdown from 'react-markdown';
import { getTextColorHex } from '../utils/color-contrast';
import { IconByName } from '../components/IconPicker';
import { formatDate, formatTime } from '../utils/date';
import { formatPrice } from '../utils/participant-types';
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  ExternalLink,
  ArrowLeft,
  Phone,
  Mail,
  Loader2,
  Handshake,
  User,
  CheckCircle2,
  XCircle,
  Heart,
  HandHelping,
} from 'lucide-react';

const MapDisplay = lazy(() => import('../components/MapDisplay'));

function buildEventJsonLd(activity: ActivityDetail, description: string) {
  const url = `https://youthnited.ro/eveniment/${activity.slug}`;
  const image = activity.banner_image ? mediaUrl(activity.banner_image) : 'https://youthnited.ro/og-image.png';

  const location: Record<string, unknown> = activity.location
    ? {
        '@type': 'Place',
        name: activity.location_text || 'Alba Iulia',
        geo: {
          '@type': 'GeoCoordinates',
          latitude: activity.location.coordinates[1],
          longitude: activity.location.coordinates[0],
        },
        address: { '@type': 'PostalAddress', addressLocality: 'Alba Iulia', addressCountry: 'RO' },
      }
    : {
        '@type': 'Place',
        name: activity.location_text || 'Alba Iulia',
        address: { '@type': 'PostalAddress', addressLocality: 'Alba Iulia', addressCountry: 'RO' },
      };

  const organizer = activity.partners.length > 0
    ? activity.partners.map((p) => ({ '@type': 'Organization', name: p.legal_name || p.name }))
    : [{ '@type': 'Organization', name: 'Alba Iulia — Capitala Tineretului din România 2026', url: 'https://youthnited.ro/' }];

  const offers = activity.requires_registration
    ? {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'RON',
        availability: 'https://schema.org/InStock',
        url,
        ...(activity.registration_deadline ? { validThrough: activity.registration_deadline } : {}),
      }
    : { '@type': 'Offer', price: '0', priceCurrency: 'RON', url };

  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: activity.title,
    description,
    startDate: activity.start_datetime,
    ...(activity.end_datetime ? { endDate: activity.end_datetime } : {}),
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location,
    image,
    url,
    organizer,
    offers,
    isAccessibleForFree: !activity.requires_registration,
  };
}

function RegistrationCTA({
  activity,
  onRegisterClick,
  onCancelClick,
}: {
  activity: ActivityDetail;
  onRegisterClick: () => void;
  onCancelClick: () => void;
}) {
  if (!activity.requires_registration) {
    return (
      <div className="rounded-xl bg-gray-100 px-5 py-4 text-center">
        <p className="text-sm font-medium text-muted-foreground" style={{ fontFamily: 'var(--font-primary)' }}>
          Acces liber
        </p>
      </div>
    );
  }

  // User is registered
  if (activity.registration_status === 'confirmed') {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-center gap-2 rounded-xl bg-green-50 px-5 py-4">
          <CheckCircle2 className="h-5 w-5 text-green-600" />
          <span
            className="text-sm font-semibold text-green-700"
            style={{ fontFamily: 'var(--font-primary)' }}
          >
            Ești înregistrat/ă
          </span>
        </div>
        <button
          onClick={onCancelClick}
          className="w-full rounded-xl border border-red-200 px-5 py-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
          style={{ fontFamily: 'var(--font-primary)' }}
        >
          Anulează înregistrarea
        </button>
      </div>
    );
  }

  // User cancelled
  if (activity.registration_status === 'cancelled') {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-center gap-2 rounded-xl bg-red-50 px-5 py-4">
          <XCircle className="h-5 w-5 text-red-500" />
          <span
            className="text-sm font-semibold text-red-600"
            style={{ fontFamily: 'var(--font-primary)' }}
          >
            Înregistrare anulată
          </span>
        </div>
        <button
          onClick={onRegisterClick}
          className="w-full rounded-xl bg-[var(--vivid-orange)] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[var(--vivid-orange)]/90"
          style={{ fontFamily: 'var(--font-primary)' }}
        >
          Înregistrează-te din nou
        </button>
      </div>
    );
  }

  const deadlinePassed = activity.registration_deadline && new Date(activity.registration_deadline) < new Date();

  if (deadlinePassed) {
    return (
      <div className="rounded-xl bg-gray-100 px-5 py-4 text-center">
        <button disabled className="w-full rounded-lg bg-gray-300 px-6 py-3 text-sm font-semibold text-white" style={{ fontFamily: 'var(--font-primary)' }}>
          Înregistrarea s-a încheiat pe {formatDate(activity.registration_deadline!)}
        </button>
      </div>
    );
  }

  const isFull = activity.participant_limit && activity.confirmed_count >= activity.participant_limit;

  if (isFull) {
    return (
      <div className="rounded-xl bg-gray-100 px-5 py-4 text-center">
        <p className="text-sm font-medium text-muted-foreground" style={{ fontFamily: 'var(--font-primary)' }}>
          Locurile disponibile sunt ocupate
        </p>
      </div>
    );
  }

  if (activity.registration_type === 'external' && activity.registration_link) {
    return (
      <div className="space-y-2">
        <a
          href={activity.registration_link}
          target="_blank"
          rel="noopener noreferrer"
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--vivid-orange)] px-6 py-4 text-sm font-bold text-white transition-colors hover:bg-[var(--vivid-orange)]/90"
          style={{ fontFamily: 'var(--font-primary)' }}
        >
          <ExternalLink className="h-4 w-4" />
          Înregistrează-te (link extern)
        </a>
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-center text-xs text-amber-700">
          Vei fi redirecționat/ă către un site extern. Datele tale vor fi gestionate de organizator, nu de platforma noastră. Nu ne asumăm responsabilitatea pentru prelucrarea datelor pe site-uri externe.
        </p>
      </div>
    );
  }

  // Block registration button if user is pending/approved volunteer
  if (activity.volunteer_status === 'pending' || activity.volunteer_status === 'approved') {
    return null;
  }

  return (
    <button
      onClick={onRegisterClick}
      className="w-full rounded-xl bg-[var(--vivid-orange)] px-6 py-4 text-sm font-bold text-white transition-colors hover:bg-[var(--vivid-orange)]/90"
      style={{ fontFamily: 'var(--font-primary)' }}
    >
      Înregistrează-te
    </button>
  );
}

function VolunteerCTA({
  activity,
  user,
  onApply,
  onWithdraw,
  applying,
}: {
  activity: ActivityDetail;
  user: { profile?: { is_volunteer?: boolean; volunteer_bio?: string } | null } | null;
  onApply: (data: { motivation?: string; team_id?: number | null; visibility_consent?: boolean }) => void;
  onWithdraw: () => void;
  applying: boolean;
}) {
  const [showForm, setShowForm] = useState(false);
  const [motivation, setMotivation] = useState('');
  const [selectedTeam, setSelectedTeam] = useState<number | ''>('');
  const [teams, setTeams] = useState<{ id: number; name: string; icon: string; color: string; capacity: number | null; confirmed_count: number }[]>([]);
  const [teamsLoaded, setTeamsLoaded] = useState(false);
  const [visibilityConsent, setVisibilityConsent] = useState(false);
  const [confirmedData, setConfirmedData] = useState<{
    coordinators: { first_name: string; last_name: string }[];
    teams: { team_name: string | null; volunteers: { first_name: string; last_name: string; source_organization: string }[] }[];
  } | null>(null);
  const [confirmedLoaded, setConfirmedLoaded] = useState(false);
  const isVolunteer = !!user?.profile?.is_volunteer;

  // Load confirmed volunteers on mount for any volunteer viewing the activity
  useEffect(() => {
    if (!activity.volunteer_applications_open || !user || !isVolunteer) return;
    if (confirmedLoaded) return;
    volunteerService.confirmedVolunteers(activity.slug)
      .then(setConfirmedData)
      .catch(() => {})
      .finally(() => setConfirmedLoaded(true));
  }, [activity.slug, activity.volunteer_applications_open, user, isVolunteer, confirmedLoaded]);

  if (!activity.volunteer_applications_open) return null;
  if (!user || !isVolunteer) return null;
  if (activity.registration_status === 'confirmed') return null;

  const confirmedSection = confirmedData && (confirmedData.coordinators.length > 0 || confirmedData.teams.length > 0) ? (
    <div className="rounded-lg border bg-white p-3 text-xs" style={{ fontFamily: 'var(--font-secondary)' }}>
      {confirmedData.coordinators.length > 0 && (
        <div className="mb-2">
          <p className="font-semibold text-muted-foreground mb-1" style={{ fontFamily: 'var(--font-primary)' }}>Coordonatori:</p>
          <div className="flex flex-wrap gap-1.5">
            {confirmedData.coordinators.map((c, i) => (
              <span key={i} className="rounded-full bg-[var(--vivid-purple)]/10 px-2 py-0.5 text-[11px] font-medium text-[var(--vivid-purple)]">
                {c.first_name} {c.last_name}
              </span>
            ))}
          </div>
        </div>
      )}
      {confirmedData.teams.map((group, i) => (
        <div key={i} className="mb-1.5 last:mb-0">
          <p className="font-semibold text-muted-foreground mb-1" style={{ fontFamily: 'var(--font-primary)' }}>
            {group.team_name || 'Voluntari confirmați'}:
          </p>
          <div className="flex flex-wrap gap-1.5">
            {group.volunteers.map((v, j) => (
              <span key={j} className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700">
                {v.first_name} {v.last_name}
                {v.source_organization && <span className="text-green-600/70"> · {v.source_organization}</span>}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  ) : null;

  if (activity.volunteer_status === 'approved') {
    return (
      <div className="space-y-2">
        {confirmedSection}
        <div className="flex items-center justify-center gap-2 rounded-xl bg-green-50 px-5 py-4">
          <HandHelping className="h-5 w-5 text-green-600" />
          <span className="text-sm font-semibold text-green-700" style={{ fontFamily: 'var(--font-primary)' }}>
            Ești voluntar aprobat pentru această activitate
          </span>
        </div>
        <button
          onClick={onWithdraw}
          className="w-full rounded-xl border border-red-200 px-5 py-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
          style={{ fontFamily: 'var(--font-primary)' }}
        >
          Retrage-te din voluntariat
        </button>
      </div>
    );
  }

  if (activity.volunteer_status === 'withdrawn') {
    return (
      <div className="flex items-center justify-center gap-2 rounded-xl bg-gray-100 px-5 py-4">
        <HandHelping className="h-5 w-5 text-muted-foreground" />
        <span className="text-sm font-semibold text-muted-foreground" style={{ fontFamily: 'var(--font-primary)' }}>
          Te-ai retras din voluntariat
        </span>
      </div>
    );
  }

  if (activity.volunteer_status === 'pending') {
    return (
      <div className="space-y-2">
        {confirmedSection}
        <div className="flex items-center justify-center gap-2 rounded-xl bg-amber-50 px-5 py-4">
          <HandHelping className="h-5 w-5 text-amber-600" />
          <span className="text-sm font-semibold text-amber-700" style={{ fontFamily: 'var(--font-primary)' }}>
            Aplicația ta de voluntariat este în evaluare
          </span>
        </div>
        <button
          onClick={onWithdraw}
          className="w-full rounded-xl border border-red-200 px-5 py-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
          style={{ fontFamily: 'var(--font-primary)' }}
        >
          Retrage aplicația
        </button>
      </div>
    );
  }

  if (activity.volunteer_status === 'rejected') {
    return (
      <div className="flex items-center justify-center gap-2 rounded-xl bg-red-50 px-5 py-4">
        <XCircle className="h-5 w-5 text-red-500" />
        <span className="text-sm font-semibold text-red-600" style={{ fontFamily: 'var(--font-primary)' }}>
          Aplicația ta pentru voluntariat a fost respinsă
        </span>
      </div>
    );
  }

  const nav = useNavigate();

  async function handleShowForm() {
    // Redirect to profile if bio is empty
    if (!user?.profile?.volunteer_bio?.trim()) {
      nav(`/cont?volunteer_bio_required=${encodeURIComponent(activity.slug)}`);
      return;
    }
    setShowForm(true);
    if (!teamsLoaded) {
      try {
        const t = await volunteerService.activityTeams(activity.slug);
        setTeams(t);
      } catch { /* no teams available */ }
      setTeamsLoaded(true);
    }
    if (!confirmedLoaded) {
      try {
        const data = await volunteerService.confirmedVolunteers(activity.slug);
        setConfirmedData(data);
      } catch { /* ignore */ }
      setConfirmedLoaded(true);
    }
  }

  if (!showForm) {
    return (
      <div className="space-y-2">
        {confirmedSection}
        <button
          onClick={handleShowForm}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-green-600 px-6 py-4 text-sm font-bold text-white transition-colors hover:bg-green-700"
          style={{ fontFamily: 'var(--font-primary)' }}
        >
          <HandHelping className="h-5 w-5" />
          Aplică ca voluntar
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-green-50/50 p-4 space-y-3">
      <p className="text-sm font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
        Aplică ca voluntar
      </p>

      {teams.length > 0 && (
        <div>
          <label className="mb-2 block text-xs font-medium text-muted-foreground">Echipa preferată (opțional)</label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <button
              type="button"
              onClick={() => setSelectedTeam('')}
              className={`rounded-lg border-2 p-3 text-left transition-all ${
                selectedTeam === '' ? 'border-[var(--vivid-purple)] bg-[var(--vivid-purple)]/5 ring-1 ring-[var(--vivid-purple)]/20' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <span className="text-xs font-medium" style={{ fontFamily: 'var(--font-primary)' }}>Fără preferință</span>
            </button>
            {teams.map((t) => {
              const isFull = t.capacity != null && t.confirmed_count >= t.capacity;
              const isSelected = selectedTeam === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  disabled={isFull}
                  onClick={() => setSelectedTeam(t.id)}
                  className={`rounded-lg border-2 p-3 text-left transition-all ${
                    isFull
                      ? 'cursor-not-allowed border-gray-100 opacity-50'
                      : isSelected
                      ? 'border-[var(--vivid-purple)] bg-[var(--vivid-purple)]/5 ring-1 ring-[var(--vivid-purple)]/20'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="mb-1.5 flex items-center gap-2">
                    <div
                      className="flex h-7 w-7 items-center justify-center rounded-md"
                      style={{ backgroundColor: t.color ? `${t.color}20` : '#f0f0f0' }}
                    >
                      {t.icon ? (
                        <IconByName name={t.icon} className="h-4 w-4" style={{ color: t.color || '#666' }} />
                      ) : (
                        <Users className="h-4 w-4" style={{ color: t.color || '#666' }} />
                      )}
                    </div>
                    <span className="text-xs font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>{t.name}</span>
                  </div>
                  <span className="text-[10px] text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                    {isFull ? 'Complet' : t.capacity != null ? `${t.confirmed_count}/${t.capacity} locuri` : 'Locuri nelimitate'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">Motivație (opțional)</label>
        <textarea
          value={motivation}
          onChange={(e) => setMotivation(e.target.value)}
          rows={2}
          placeholder="De ce vrei să fii voluntar la acest eveniment?"
          className="w-full rounded-lg border px-3 py-2 text-sm"
          style={{ fontFamily: 'var(--font-secondary)' }}
        />
      </div>
      <label className="flex items-start gap-2 rounded-lg border bg-white p-3 cursor-pointer">
        <input
          type="checkbox"
          checked={visibilityConsent}
          onChange={(e) => setVisibilityConsent(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded accent-[var(--vivid-purple)]"
        />
        <span className="text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
          Sunt de acord ca numele meu să fie afișat celorlalți voluntari care vizualizează această activitate.
        </span>
      </label>
      <div className="flex gap-2">
        <button
          onClick={() => setShowForm(false)}
          className="flex-1 rounded-lg border px-4 py-2.5 text-sm font-medium text-muted-foreground hover:bg-white"
        >
          Anulează
        </button>
        <button
          onClick={() => onApply({ motivation, team_id: selectedTeam || null, visibility_consent: visibilityConsent })}
          disabled={applying}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60"
        >
          <HandHelping className="h-4 w-4" />
          {applying ? 'Se trimite...' : 'Trimite aplicația'}
        </button>
      </div>
    </div>
  );
}

export default function EventDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activity, setActivity] = useState<ActivityDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showRegModal, setShowRegModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [applyingVolunteer, setApplyingVolunteer] = useState(false);

  function loadActivity() {
    if (!slug) return;
    setLoading(true);
    activitiesService.detail(slug)
      .then((a) => {
        setActivity(a);
        setBookmarked(a.is_bookmarked);
      })
      .catch(() => setError('Activitatea nu a fost găsită.'))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadActivity();
  }, [slug]);

  function handleRegisterClick() {
    if (!user) {
      setShowLoginPrompt(true);
    } else {
      setShowRegModal(true);
    }
  }

  async function handleBookmarkToggle() {
    if (!slug || !user) return;
    const wasBookmarked = bookmarked;
    setBookmarked(!wasBookmarked);
    try {
      if (wasBookmarked) {
        await bookmarkService.remove(slug);
      } else {
        await bookmarkService.add(slug);
      }
    } catch {
      setBookmarked(wasBookmarked);
    }
  }

  async function handleVolunteerApply(data: { motivation?: string; team_id?: number | null; visibility_consent?: boolean }) {
    if (!slug || applyingVolunteer) return;
    setApplyingVolunteer(true);
    try {
      await volunteerService.apply(slug, data);
      loadActivity();
    } catch (err: any) {
      setError(err?.message || 'Eroare la trimiterea aplicației.');
    } finally {
      setApplyingVolunteer(false);
    }
  }

  async function handleVolunteerWithdraw() {
    if (!slug) return;
    try {
      await volunteerService.withdraw(slug);
      loadActivity();
    } catch {
      // silently fail
    }
  }

  async function handleCancelClick() {
    if (!slug || cancelling) return;
    setCancelling(true);
    try {
      await registrationService.cancel(slug);
      loadActivity();
    } catch {
      // silently fail
    } finally {
      setCancelling(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--vivid-purple)]" />
      </div>
    );
  }

  if (error || !activity) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <p className="mb-4 text-lg font-semibold">{error || 'Activitate negăsită'}</p>
        <button onClick={() => navigate('/evenimente')} className="text-sm font-medium text-[var(--vivid-purple)] hover:underline">
          Înapoi la evenimente
        </button>
      </div>
    );
  }

  const mainTags = activity.tags.filter((t) => t.is_main_tag);
  const otherTags = activity.tags.filter((t) => !t.is_main_tag);
  const hasLocation = activity.location && activity.location.coordinates;

  // Age restriction text
  let ageText = '';
  if (activity.min_age && activity.max_age) {
    ageText = `Vârstă: ${activity.min_age}–${activity.max_age} ani`;
  } else if (activity.min_age) {
    ageText = `Vârstă minimă: ${activity.min_age} ani`;
  } else if (activity.max_age) {
    ageText = `Vârstă maximă: ${activity.max_age} ani`;
  }

  // Spots info — show free places remaining ("X locuri libere"); hidden when capacity is unlimited
  let spotsText: string | null = null;
  if (activity.slots_remaining !== null) {
    if (activity.slots_remaining === 0) {
      spotsText = 'Nu mai sunt locuri libere';
    } else if (activity.slots_remaining === 1) {
      spotsText = '1 loc liber';
    } else {
      spotsText = `${activity.slots_remaining} locuri libere`;
    }
  }

  const description = activity.description
    ? activity.description.slice(0, 160).replace(/[#*_]/g, '')
    : `${activity.title} — ${activity.location_text}`;
  const eventJsonLd = buildEventJsonLd(activity, description);

  return (
    <div className="mx-auto max-w-3xl">
      <DocumentMeta
        title={activity.title}
        description={description}
        image={activity.banner_image ? mediaUrl(activity.banner_image) : undefined}
        url={`/eveniment/${activity.slug}`}
        type="article"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(eventJsonLd)}</script>
      </Helmet>
      {/* Banner */}
      <div className="relative h-56 bg-gradient-to-br from-[var(--vivid-purple)]/30 to-[var(--vivid-cyan)]/10 sm:h-72">
        {activity.banner_image && (
          <img
            src={mediaUrl(activity.banner_image)}
            alt={activity.title}
            fetchPriority="high"
            decoding="async"
            className="h-full w-full object-cover"
          />
        )}
        <button
          onClick={() => navigate(-1)}
          className="absolute left-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/80 shadow backdrop-blur transition-colors hover:bg-white"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        {user && (
          <button
            onClick={handleBookmarkToggle}
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/80 shadow backdrop-blur transition-all hover:bg-white hover:scale-110 active:scale-95"
            aria-label={bookmarked ? 'Elimină de la favorite' : 'Adaugă la favorite'}
          >
            <Heart
              className="h-5 w-5 transition-all"
              fill={bookmarked ? '#ff6417' : 'none'}
              stroke={bookmarked ? '#ff6417' : '#1a1a1a'}
            />
          </button>
        )}
      </div>

      <div className="px-4 py-6">
        {/* Main tags */}
        {mainTags.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {mainTags.map((t) => (
              <span
                key={t.id}
                className="rounded-full px-3 py-1 text-xs font-semibold"
                style={{ backgroundColor: t.color || '#3b0b86', color: getTextColorHex(t.color || '#3b0b86') }}
              >
                {t.name}
              </span>
            ))}
          </div>
        )}

        {/* Title */}
        <h1 className="mb-4 text-2xl font-bold leading-tight sm:text-3xl" style={{ fontFamily: 'var(--font-primary)' }}>
          {activity.title}
        </h1>

        {/* Date & Time */}
        <div className="mb-4 space-y-2">
          <div className="flex items-center gap-3 text-sm" style={{ fontFamily: 'var(--font-secondary)' }}>
            <Calendar className="h-4 w-4 text-[var(--vivid-purple)]" />
            <span>
              {formatDate(activity.start_datetime)}
              {activity.is_multi_day && activity.end_datetime && ` — ${formatDate(activity.end_datetime)}`}
            </span>
          </div>
          <div className="flex items-center gap-3 text-sm" style={{ fontFamily: 'var(--font-secondary)' }}>
            <Clock className="h-4 w-4 text-[var(--vivid-purple)]" />
            <span>
              {formatTime(activity.start_datetime)}
              {activity.end_datetime && ` — ${formatTime(activity.end_datetime)}`}
            </span>
          </div>
          {activity.location_text && (
            <div className="flex items-center gap-3 text-sm" style={{ fontFamily: 'var(--font-secondary)' }}>
              <MapPin className="h-4 w-4 text-[var(--vivid-purple)]" />
              <span>{activity.location_text}</span>
            </div>
          )}
          {spotsText && (
            <div className="flex items-center gap-3 text-sm" style={{ fontFamily: 'var(--font-secondary)' }}>
              <Users className="h-4 w-4 text-[var(--vivid-purple)]" />
              <span>{spotsText}</span>
            </div>
          )}
        </div>

        {/* Participant types — only when activity uses internal registration */}
        {activity.requires_registration &&
          activity.registration_type === 'internal' &&
          activity.participant_types &&
          activity.participant_types.length > 0 &&
          (() => {
            const anyPaid = activity.participant_types.some((t) => parseFloat(t.price) > 0);
            // When the activity has only the auto-default "Participant" type and zero customization,
            // skip the section so we don't show a redundant card on simple events.
            const onlyImplicitDefault =
              activity.participant_types.length === 1 &&
              activity.participant_types[0].title === 'Participant' &&
              parseFloat(activity.participant_types[0].price) === 0 &&
              !activity.participant_types[0].description;
            if (onlyImplicitDefault) return null;
            return (
              <section className="mb-6 rounded-xl border bg-white p-4">
                <h2 className="mb-3 text-base font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
                  Tipuri de participanți
                </h2>
                <div className="space-y-3">
                  {activity.participant_types.map((t) => {
                    const slotsLabel =
                      t.slots_remaining === null
                        ? 'Locuri nelimitate'
                        : t.slots_remaining === 0
                          ? 'Locuri ocupate'
                          : `${t.slots_remaining} ${t.slots_remaining === 1 ? 'loc disponibil' : 'locuri disponibile'}`;
                    return (
                      <div key={t.id} className="rounded-lg border bg-gray-50/60 p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
                              {t.title}
                            </p>
                            <p className="text-xs text-muted-foreground">{slotsLabel}</p>
                          </div>
                          <span className="rounded-full bg-[var(--vivid-yellow-green)]/30 px-3 py-1 text-xs font-semibold text-[var(--vivid-purple)]">
                            {formatPrice(t.price)}
                          </span>
                        </div>
                        {t.description && (
                          <div
                            className="prose prose-sm mt-2 max-w-none text-xs text-muted-foreground"
                            style={{ fontFamily: 'var(--font-secondary)' }}
                          >
                            <ReactMarkdown>{t.description}</ReactMarkdown>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {anyPaid && (
                  <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                    <p className="mb-1 font-semibold">Metode de plată</p>
                    <p className="mb-2">Plățile se gestionează direct cu organizatorul — noi nu procesăm plăți.</p>
                    {activity.payment_methods && (
                      <div className="prose prose-sm max-w-none">
                        <ReactMarkdown>{activity.payment_methods}</ReactMarkdown>
                      </div>
                    )}
                  </div>
                )}
              </section>
            );
          })()}

        {/* Registration CTA */}
        <div className="mb-6 space-y-3">
          <RegistrationCTA
            activity={activity}
            onRegisterClick={handleRegisterClick}
            onCancelClick={handleCancelClick}
          />
          <VolunteerCTA
            activity={activity}
            user={user}
            onApply={handleVolunteerApply}
            onWithdraw={handleVolunteerWithdraw}
            applying={applyingVolunteer}
          />
        </div>

        {/* Info badges */}
        <div className="mb-6 flex flex-wrap gap-3">
          {ageText && (
            <div className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-xs font-medium" style={{ fontFamily: 'var(--font-secondary)' }}>
              {ageText}
            </div>
          )}
          {activity.minors_require_adult && (
            <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700" style={{ fontFamily: 'var(--font-secondary)' }}>
              Minorii necesită însoțitor adult
            </div>
          )}
        </div>

        {/* Other tags */}
        {otherTags.length > 0 && (
          <div className="mb-6 flex flex-wrap gap-2">
            {otherTags.map((t) => (
              <span key={t.id} className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-muted-foreground">
                {t.name}
              </span>
            ))}
          </div>
        )}

        {/* Description */}
        {activity.description && (
          <div className="mb-8">
            <div className="prose prose-sm max-w-none" style={{ fontFamily: 'var(--font-secondary)' }}>
              <ReactMarkdown>{activity.description}</ReactMarkdown>
            </div>
          </div>
        )}

        {/* Map */}
        {hasLocation && (
          <div className="mb-8 h-64 overflow-hidden rounded-xl border">
            <Suspense fallback={<div className="flex h-full items-center justify-center bg-gray-50 text-sm text-muted-foreground">Se încarcă harta...</div>}>
              <MapDisplay lat={activity.location!.coordinates[1]} lng={activity.location!.coordinates[0]} />
            </Suspense>
          </div>
        )}

        {/* Partners */}
        {activity.partners.length > 0 && (
          <section className="mb-8">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
              <Handshake className="h-5 w-5 text-[var(--vivid-purple)]" />
              Organizatori
            </h2>
            <div className="space-y-3">
              {activity.partners.map((p) => (
                <div key={p.id} className="rounded-xl border bg-white p-4">
                  <div className="flex items-center gap-3">
                    {p.logo ? (
                      <img src={mediaUrl(p.logo)} alt="" className="h-10 w-10 rounded-lg border object-cover" />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--vivid-purple)]/10">
                        <Handshake className="h-5 w-5 text-[var(--vivid-purple)]" />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>{p.name}</p>
                      {p.contact_name && (
                        <p className="text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                          {p.contact_name}
                        </p>
                      )}
                    </div>
                  </div>
                  {(p.contact_phone || p.contact_email) && (
                    <div className="mt-2 flex flex-wrap gap-3 pl-13">
                      {p.contact_phone && (
                        <a href={`tel:${p.contact_phone}`} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                          <Phone className="h-3 w-3" /> {p.contact_phone}
                        </a>
                      )}
                      {p.contact_email && (
                        <a href={`mailto:${p.contact_email}`} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                          <Mail className="h-3 w-3" /> {p.contact_email}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Activity Contacts */}
        {activity.contacts.length > 0 && (
          <section className="mb-8">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
              <User className="h-5 w-5 text-[var(--vivid-purple)]" />
              Contact
            </h2>
            <div className="space-y-3">
              {activity.contacts.map((c) => (
                <div key={c.id} className="rounded-xl border bg-white p-4">
                  <p className="text-sm font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>{c.name}</p>
                  <div className="mt-1 flex flex-wrap gap-3">
                    {c.phone && (
                      <a href={`tel:${c.phone}`} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                        <Phone className="h-3 w-3" /> {c.phone}
                      </a>
                    )}
                    {c.email && (
                      <a href={`mailto:${c.email}`} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                        <Mail className="h-3 w-3" /> {c.email}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Modals */}
      <LoginPromptModal
        open={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        onSuccess={() => {
          setShowLoginPrompt(false);
          setShowRegModal(true);
        }}
      />

      <RegistrationModal
        open={showRegModal}
        slug={slug || ''}
        partnerNames={activity?.partners?.map(p => p.legal_name || p.name) || []}
        fieldConfig={activity?.registration_fields}
        participantTypes={activity?.participant_types || []}
        paymentMethods={activity?.payment_methods || ''}
        onClose={() => setShowRegModal(false)}
        onSuccess={() => {
          setShowRegModal(false);
          loadActivity();
        }}
      />
    </div>
  );
}
